/**
 * Notifications (S12).
 *
 * Two things generate them and they behave differently, which is why the shape
 * carries a `kind`:
 *
 * **Call alerts** are one per interaction — a caller who asked for a person or a
 * call back, or who left upset. They are facts about a conversation that
 * already happened, so they never change and never need re-evaluating.
 *
 * **Usage alerts** are one per threshold crossed per month. They come from a
 * poll, which is the reason `dedupeKey` exists: without it an hourly check of a
 * budget sitting at 82% would produce twenty-four identical alerts a day.
 *
 * Read state is per user and stored server-side, because S12 asks for it and
 * because the alternative — component state, as the mock has — loses the
 * distinction the moment anybody reloads.
 */
import { z } from 'zod';

import { NotificationKind, NotificationLinkType, NotificationSeverity } from './enums.js';

/**
 * The floor on how many the list returns.
 *
 * Every unread alert is returned, however many there are — an operator must never
 * have to guess whether the panel is showing all of them. Below that floor the
 * list is topped up with the most recent read ones, so a quiet account still has
 * some history to look at instead of an empty panel.
 */
export const NOTIFICATION_FLOOR = 20;

export const notificationSchema = z.object({
  id: z.string().uuid(),
  kind: NotificationKind.schema,
  severity: NotificationSeverity.schema,
  title: z.string(),
  body: z.string().nullable(),
  /**
   * Where the alert points, if anywhere.
   *
   * A call alert an operator cannot act on from the panel is a notification that
   * has told them to go and find something. `NONE` is honest for usage alerts,
   * which are about the account rather than about a record.
   */
  linkType: NotificationLinkType.schema,
  linkId: z.string().nullable(),
  createdAt: z.string().datetime(),
  /** Per the signed-in user, not globally. */
  read: z.boolean(),
});
export type Notification = z.infer<typeof notificationSchema>;

export const notificationListSchema = z.object({
  items: z.array(notificationSchema),
  /**
   * Unread across everything, not just the returned page.
   *
   * The badge is a claim about the account, so counting only what fits on one
   * page would make it quietly wrong exactly when it matters most.
   */
  unread: z.number().int().min(0),
});
export type NotificationList = z.infer<typeof notificationListSchema>;

export const markReadSchema = z.object({
  /** Absent means every notification currently unread for this user. */
  ids: z.array(z.string().uuid()).optional(),
});
export type MarkRead = z.infer<typeof markReadSchema>;
